import React from 'react';
import type { FC, ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';

import {
	ContentStateStateContainer,
	EditorContentState,
	RendererContentState,
} from '@confluence/content-state';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import { LivePagesByLine } from './LivePagesByLine';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LivePagesByLineWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div({
	flexGrow: 1,
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightArea = styled.div({
	display: 'flex',
	minHeight: token('space.400', '32px'),
	alignItems: 'center',
});

export type LivePagesByLineAreaProps = {
	contentId: string;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	editorAppearanceButton: ReactNode;
	isLivePage: boolean;
	isKnowledgeBaseSpace: boolean;
	viewMode?: boolean;
};

export const LivePagesByLineArea: FC<LivePagesByLineAreaProps> = ({
	contentId,
	contentType,
	isPublishingContent,
	userOffline,
	editorAppearanceButton,
	isLivePage,
	isKnowledgeBaseSpace,
	viewMode,
}) => {
	const isCustomSitesPageTitleFFOn = useBooleanFeatureFlag(
		'confluence.frontend.custom-sites.page-header-and-title',
	);
	const isNewLivePageBylineFeatureFlagEnabled = useBooleanFeatureFlag(
		'confluence.frontend.custom-sites.new-live-pages-byline',
	);
	const isNewLivePageByline =
		isCustomSitesPageTitleFFOn && isLivePage && isNewLivePageBylineFeatureFlagEnabled;

	const toolbarButtonStyle = isNewLivePageByline && { height: '24px', alignItems: 'center' };
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	return (
		<LivePagesByLineWrapper data-vc="live-pages-byline-wrapper" {...ssrPlaceholderIdProp}>
			{isLivePage && (
				<LivePagesByLine
					contentId={contentId}
					contentType={contentType}
					isPublishingContent={isPublishingContent}
					userOffline={userOffline}
					isKnowledgeBaseSpace={isKnowledgeBaseSpace}
					viewMode={viewMode}
				/>
			)}
			{!isNewLivePageByline && (
				<>
					<Spacer />
					<RightArea data-testId="live-page-byline-right-area">
						<Subscribe to={[ContentStateStateContainer]}>
							{(contentStateState: ContentStateStateContainer) => {
								// view mode users should not be able to edit the content state, so we just render the content state.
								if (viewMode) {
									return (
										<RendererContentState
											contentId={contentId}
											hasSeparator={false}
											isPagePreview // this sets the query to use draft version over published version
										/>
									);
								}
								// Knowledge space live pages will always show the editable content state component.
								// Other live pages will only show the editable content state component if the content state is already set.
								// This is because a page without a content state is initialized by the ContentStateHoverButton component.
								return (
									(contentStateState.state.contentState || isKnowledgeBaseSpace) && (
										<EditorContentState
											contentId={contentId}
											contentType={contentType}
											isPublishingContent={isPublishingContent}
											userOffline={userOffline}
											floatingToolbarButtonStyle={toolbarButtonStyle}
											isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
										/>
									)
								);
							}}
						</Subscribe>
						{!viewMode && editorAppearanceButton}
					</RightArea>
				</>
			)}
		</LivePagesByLineWrapper>
	);
};
